import { RecurringTimeEntry } from '../services/types';

export const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const DAY_NAMES_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const NTH_LABELS = ['1st', '2nd', '3rd', '4th', '5th'];

/** Compute dates matching a template's frequency for a given month */
export function getFrequencyDatesForMonth(template: RecurringTimeEntry, year: number, month: number): string[] {
  const dates: string[] = [];
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const pad = (n: number) => String(n).padStart(2, '0');

  if (template.frequency === 'biweekly') {
    const anchor = template.biweeklyAnchor ? new Date(template.biweeklyAnchor + 'T00:00:00') : new Date();
    const targetDow = template.dayOfWeek ?? 0;
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      if (date.getDay() !== targetDow) continue;
      const diffDays = Math.round((date.getTime() - anchor.getTime()) / 86400000);
      if (diffDays >= 0 && diffDays % 14 === 0) {
        dates.push(`${year}-${pad(month + 1)}-${pad(d)}`);
      }
    }
    return dates;
  }

  if (template.frequency === 'monthly' && template.monthlyPattern === 'nthWeekday') {
    const targetDow = template.dayOfWeek ?? 0;
    const occurrences: number[] = [];
    for (let d = 1; d <= daysInMonth; d++) {
      if (new Date(year, month, d).getDay() === targetDow) occurrences.push(d);
    }
    const idx = (template.nthOccurrence ?? 1) - 1;
    if (idx >= 0 && idx < occurrences.length) {
      dates.push(`${year}-${pad(month + 1)}-${pad(occurrences[idx])}`);
    }
    return dates;
  }

  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month, d);
    const dow = date.getDay();
    const dateStr = `${year}-${pad(month + 1)}-${pad(d)}`;
    if (template.frequency === 'daily') {
      if (dow >= 1 && dow <= 5) dates.push(dateStr);
    } else if (template.frequency === 'weekly') {
      if (dow === (template.dayOfWeek ?? 1)) dates.push(dateStr);
    } else if (template.frequency === 'monthly') {
      if (d === (template.dayOfMonth ?? 1)) dates.push(dateStr);
    }
  }
  return dates;
}

/** Get per-occurrence label like "1h/wk" */
export function getPerLabel(frequency: string, hours: number): string {
  const h = hours % 1 === 0 ? String(hours) : hours.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
  if (frequency === 'daily') return `${h}h/day`;
  if (frequency === 'weekly') return `${h}h/wk`;
  if (frequency === 'biweekly') return `${h}h/2wk`;
  return `${h}h/mo`;
}

/** Compute monthly total hours for a single template */
export function getTemplateMonthlyHours(t: RecurringTimeEntry): number {
  const now = new Date();
  const occurrences = getFrequencyDatesForMonth(t, now.getFullYear(), now.getMonth()).length;
  if (t.monthlyHoursCap && t.monthlyHoursCap > 0) return t.monthlyHoursCap;
  if (t.templateType === 'activity' && t.clients) {
    const totalPerOccurrence = t.clients.reduce((sum, c) => sum + c.hoursWorked, 0);
    return totalPerOccurrence * occurrences;
  }
  return (t.hoursWorked ?? 0) * occurrences;
}

/** Enhanced description with per-occurrence + monthly totals */
export function getEnhancedDescription(t: RecurringTimeEntry): string {
  const now = new Date();
  const occurrences = getFrequencyDatesForMonth(t, now.getFullYear(), now.getMonth()).length;

  let freq: string;
  if (t.frequency === 'daily') {
    freq = 'Daily';
  } else if (t.frequency === 'weekly') {
    freq = `Weekly (${DAY_NAMES[t.dayOfWeek ?? 1]})`;
  } else if (t.frequency === 'biweekly') {
    freq = `Bi-weekly (${DAY_NAMES_FULL[t.dayOfWeek ?? 0]})`;
  } else if (t.frequency === 'monthly' && t.monthlyPattern === 'nthWeekday') {
    freq = `Monthly (${NTH_LABELS[(t.nthOccurrence ?? 1) - 1]} ${DAY_NAMES_FULL[t.dayOfWeek ?? 0]})`;
  } else {
    freq = `Monthly (Day ${t.dayOfMonth ?? 1})`;
  }

  // Cap templates: just show cap/mo
  if (t.monthlyHoursCap && t.monthlyHoursCap > 0) {
    return `${t.companyName} \u00b7 ${t.ticketNumber} \u00b7 ${t.monthlyHoursCap}h/mo \u00b7 ${freq}`;
  }

  // Activity templates
  if (t.templateType === 'activity' && t.clients) {
    const totalPerOccurrence = t.clients.reduce((sum, c) => sum + c.hoursWorked, 0);
    const monthlyTotal = totalPerOccurrence * occurrences;
    const perLabel = getPerLabel(t.frequency, totalPerOccurrence);
    const moLabel = `${monthlyTotal % 1 === 0 ? monthlyTotal : monthlyTotal.toFixed(1)}h/mo`;
    if (t.frequency === 'monthly') {
      return `${t.clients.length} clients \u00b7 ${moLabel} \u00b7 ${freq}`;
    }
    return `${t.clients.length} clients \u00b7 ${perLabel} \u00b7 ${moLabel} \u00b7 ${freq}`;
  }

  // Client templates
  const hours = t.hoursWorked ?? 0;
  const monthlyTotal = hours * occurrences;
  const perLabel = getPerLabel(t.frequency, hours);
  const moLabel = `${monthlyTotal % 1 === 0 ? monthlyTotal : monthlyTotal.toFixed(1)}h/mo`;

  if (t.frequency === 'monthly') {
    return `${t.companyName} \u00b7 ${t.ticketNumber} \u00b7 ${moLabel} \u00b7 ${freq}`;
  }
  return `${t.companyName} \u00b7 ${t.ticketNumber} \u00b7 ${perLabel} \u00b7 ${moLabel} \u00b7 ${freq}`;
}

/** Get frequency summary text for display */
export function getFrequencySummary(t: RecurringTimeEntry): string {
  if (t.frequency === 'daily') return 'Daily (Mon-Fri)';
  if (t.frequency === 'weekly') return `Weekly on ${DAY_NAMES_FULL[t.dayOfWeek ?? 1]}`;
  if (t.frequency === 'biweekly') return `Bi-weekly on ${DAY_NAMES_FULL[t.dayOfWeek ?? 0]}`;
  if (t.frequency === 'monthly' && t.monthlyPattern === 'nthWeekday') {
    return `Monthly (${NTH_LABELS[(t.nthOccurrence ?? 1) - 1]} ${DAY_NAMES_FULL[t.dayOfWeek ?? 0]})`;
  }
  return `Monthly (Day ${t.dayOfMonth ?? 1})`;
}
